export interface FieldSection {
  id: string;
  name: string;
  description: string;
  icon: string;
  tags: string[];
  priority: number;
  defaultExpanded?: boolean;
}

export const FIELD_SECTIONS: FieldSection[] = [
  {
    id: 'identity',
    name: 'Core Identity',
    description: 'Basic demographic and cultural identity information',
    icon: '📋',
    tags: ['identity', 'demographics', 'cultural'],
    priority: 100,
    defaultExpanded: true,
  },
  {
    id: 'medical',
    name: 'Medical & Health',
    description: 'Physical health conditions, medications, and medical history',
    icon: '🏥',
    tags: ['medical', 'health'],
    priority: 90,
  },
  {
    id: 'mental-health',
    name: 'Mental Health',
    description: 'Mental health conditions, substance use, and behavioral health',
    icon: '🧠',
    tags: ['mental-health', 'behavioral'],
    priority: 85,
  },
  {
    id: 'genetic',
    name: 'Genetic Information',
    description: 'Genetic markers, screening, and hereditary patterns',
    icon: '🧬',
    tags: ['genetic', 'hereditary'],
    priority: 80,
  },
  {
    id: 'legal',
    name: 'Legal & Estate',
    description: 'Legal status, estate planning, and guardianship',
    icon: '⚖️',
    tags: ['legal', 'estate'],
    priority: 75,
  },
  {
    id: 'financial',
    name: 'Financial',
    description: 'Income, assets, debts, and insurance',
    icon: '💰',
    tags: ['financial', 'economic'],
    priority: 70,
  },
  {
    id: 'social',
    name: 'Social & Living',
    description: 'Housing, marital status, support network, and services',
    icon: '🏠',
    tags: ['social', 'living-situation'],
    priority: 65,
  },
  {
    id: 'professional',
    name: 'Education & Work',
    description: 'Education level, occupation, and employment status',
    icon: '🎓',
    tags: ['education', 'employment', 'professional'],
    priority: 60,
  },
  {
    id: 'relationships',
    name: 'Relationships & Therapy',
    description: 'Emotional bonds, family roles, and therapeutic assessment',
    icon: '👥',
    tags: ['relationships', 'therapy', 'family-dynamics'],
    priority: 55,
  },
  {
    id: 'research',
    name: 'Research Data',
    description: 'Research identifiers, consent, data quality, and methodology',
    icon: '🔬',
    tags: ['research', 'metadata'],
    priority: 50,
  },
  {
    id: 'contact',
    name: 'Contact & Location',
    description: 'Contact information and location details',
    icon: '📍',
    tags: ['contact', 'location'],
    priority: 45,
  },
  {
    id: 'notes',
    name: 'Notes',
    description: 'Additional notes and documentation',
    icon: '📝',
    tags: ['notes', 'documentation'],
    priority: 10,
  },
];

export interface UseCaseSectionConfig {
  useCaseId: string;
  visibleSections: string[];
  expandedSections: string[];
}

export const USE_CASE_SECTION_CONFIGS: UseCaseSectionConfig[] = [
  {
    useCaseId: 'medical-genetic',
    visibleSections: ['identity', 'medical', 'mental-health', 'genetic', 'social', 'notes'],
    expandedSections: ['identity', 'medical', 'genetic'],
  },
  {
    useCaseId: 'family-therapy',
    visibleSections: ['identity', 'mental-health', 'relationships', 'social', 'notes'],
    expandedSections: ['identity', 'mental-health', 'relationships'],
  },
  {
    useCaseId: 'legal-estate',
    visibleSections: ['identity', 'legal', 'financial', 'medical', 'notes'],
    expandedSections: ['identity', 'legal', 'financial'],
  },
  {
    useCaseId: 'social-work',
    visibleSections: ['identity', 'social', 'financial', 'professional', 'medical', 'mental-health', 'notes'],
    expandedSections: ['identity', 'social', 'financial', 'professional'],
  },
  {
    useCaseId: 'general-research',
    visibleSections: ['identity', 'research', 'genetic', 'medical', 'mental-health', 'social', 'notes'],
    expandedSections: ['identity', 'research', 'genetic'],
  },
  {
    useCaseId: 'default',
    visibleSections: ['identity', 'medical', 'mental-health', 'social', 'professional', 'contact', 'notes'],
    expandedSections: ['identity'],
  },
];

/**
 * Get the section configuration for a specific use case
 */
export function getSectionConfigForUseCase(useCaseId: string | undefined): UseCaseSectionConfig {
  if (!useCaseId) {
    return USE_CASE_SECTION_CONFIGS.find(config => config.useCaseId === 'default')!;
  }

  const config = USE_CASE_SECTION_CONFIGS.find(config => config.useCaseId === useCaseId);
  return config || USE_CASE_SECTION_CONFIGS.find(config => config.useCaseId === 'default')!;
}

/**
 * Get field section by tag (primary tag determines section placement)
 */
export function getFieldSection(fieldTags: string[] | { id: string }[]): FieldSection | undefined {
  if (!fieldTags || fieldTags.length === 0) {
    return undefined;
  }

  // Find the section that matches the field's primary tag (first tag)
  const firstTag = fieldTags[0];
  const primaryTag = typeof firstTag === 'string' ? firstTag : firstTag.id;

  // Sort sections by priority to ensure consistent matching
  const sortedSections = [...FIELD_SECTIONS].sort((a, b) => b.priority - a.priority);

  // Find the first section that includes the primary tag
  return sortedSections.find(section => section.tags.includes(primaryTag));
}

/**
 * Group fields by section
 */
export function groupFieldsBySection(fields: any[]): Map<string, any[]> {
  const grouped = new Map<string, any[]>();

  for (const field of fields) {
    const section = getFieldSection(field.tags);
    if (section) {
      const sectionFields = grouped.get(section.id) || [];
      sectionFields.push(field);
      grouped.set(section.id, sectionFields);
    }
  }

  return grouped;
}
